import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { QualityOverview } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Notice, Spinner, Tile } from '../components/ui.js'
import { formatDate, formatDateTime } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { TaskModal } from './TaskDialogs.js'
import { InspectionsPanel } from './Inspections.js'

const STATUS_LABEL: Record<string, string> = { bestaetigt: 'bestätigt', verworfen: 'verworfen', unauffaellig: 'unauffällig' }

/**
 * Qualität: where something looks wrong, from three sources that do not
 * need anyone to walk the buildings. The photo check, what residents say on
 * the notice board, and places where the team repeatedly could not get in.
 *
 * Organised by object and task. There is no ranking of people on this page,
 * on purpose.
 */
export function QualityPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [all, setAll] = useState(false)
  const [days, setDays] = useState(30)
  const [openTask, setOpenTask] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: ['quality', all, days],
    queryFn: () => api<QualityOverview>(`/api/quality?days=${days}${all ? '&alle=true' : ''}`),
    placeholderData: (previous) => previous,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['quality'] })

  const checkPending = useMutation({
    mutationFn: () => api<{ checked: number }>('/api/quality/check-pending', { method: 'POST' }),
    onSuccess: async (result) => {
      toast(result.checked > 0 ? `${result.checked} Aufgaben geprüft.` : 'Nichts mehr zu prüfen.')
      await refresh()
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Prüfung fehlgeschlagen.', 'error'),
  })

  const review = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'bestaetigt' | 'verworfen' }) =>
      api(`/api/occurrences/${id}/quality-review`, { method: 'POST', body: { status } }),
    onSuccess: async (_r, { status }) => {
      toast(status === 'bestaetigt' ? 'Hinweis bestätigt.' : 'Hinweis verworfen.')
      await refresh()
    },
  })

  const data = overview.data
  const labels = data?.flagLabels ?? {}
  const hasScores = (data?.tasks ?? []).some((t) => t.qualityScore !== null && t.qualityScore !== undefined)

  return (
    <>
      <PageHead
        title="Qualität"
        subtitle="Qualitätsbegehungen, Fotoprüfung, Rückmeldungen der Bewohner und Stellen ohne Zutritt."
        action={
          <div className="actions">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Zeitraum" className="select-inline">
              <option value={7}>Letzte 7 Tage</option>
              <option value={30}>Letzte 30 Tage</option>
              <option value={90}>Letzte 90 Tage</option>
            </select>
            <Link className="btn secondary" to="/einstellungen">
              <Icon name="gear" /> Prüfung einstellen
            </Link>
          </div>
        }
      />

      <Notice kind="info">
        Bewertet wird das Ergebnis am Objekt, nicht die Person. Alle Hinweise sind Vorschläge: bestätigen oder verwerfen Sie sie, beides
        wird protokolliert.
      </Notice>

      {overview.isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          <InspectionsPanel />
          <div className="tiles" style={{ marginBottom: 18 }}>
            <Tile label="Offene Hinweise" value={data.summary.hints} tone={data.summary.hints > 0 ? 'alert' : 'good'} sub="Fotoprüfung, zu entscheiden" />
            <Tile label="Geprüft" value={data.summary.checked} sub={`${data.summary.confirmed} Hinweise bestätigt`} />
            <Tile
              label="Note aus der KI-Prüfung"
              value={data.summary.avgScore === null ? '–' : data.summary.avgScore.toFixed(1)}
              sub={data.summary.avgScore === null ? 'KI ist aus oder hat noch nichts bewertet' : 'Durchschnitt, 5 ist einwandfrei'}
            />
            <Tile
              label="Bewohner"
              value={`${data.summary.feedbackGood} / ${data.summary.feedbackBad}`}
              sub="sauber / nicht sauber"
              tone={data.summary.feedbackBad > data.summary.feedbackGood ? 'alert' : undefined}
            />
            <Tile
              label="Noch ungeprüft"
              value={data.summary.unchecked}
              sub={
                data.summary.unchecked > 0 ? (
                  <button type="button" className="linklike" onClick={() => checkPending.mutate()} disabled={checkPending.isPending}>
                    {checkPending.isPending ? 'prüft …' : 'jetzt prüfen'}
                  </button>
                ) : (
                  'erledigte Aufgaben mit Foto'
                )
              }
            />
          </div>

          <section className="card" style={{ marginBottom: 18 }}>
            <div className="card-head">
              <h2>
                <Icon name="document" size={16} /> Fotoprüfung
              </h2>
              <div className="segmented" role="group">
                <button type="button" aria-pressed={!all} onClick={() => setAll(false)}>
                  Nur offene Hinweise
                </button>
                <button type="button" aria-pressed={all} onClick={() => setAll(true)}>
                  Alle geprüften
                </button>
              </div>
            </div>
            {data.tasks.length === 0 ? (
              <EmptyState title={all ? 'Noch nichts geprüft' : 'Keine offenen Hinweise'} hint="Neue Fotos werden nach dem Abhaken automatisch geprüft." />
            ) : (
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Leistung und Ort</th>
                      <th>Hinweis</th>
                      {hasScores && <th className="nowrap">Note</th>}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.tasks.map((task) => (
                      <tr key={task.id}>
                        <td className="nowrap small">{formatDate(task.dueDate)}</td>
                        <td>
                          <button type="button" className="linklike" onClick={() => setOpenTask(task.id)}>
                            <span className="strong">{task.title}</span>
                          </button>
                          <div className="muted small">
                            <Link to={`/objekte/${task.objectId}`}>
                              {task.siteName}
                              {task.path.length ? `, ${task.path.join(' › ')}` : ''}
                            </Link>
                          </div>
                        </td>
                        <td className="small">
                          <div className="chips" style={{ marginBottom: 4 }}>
                            {(task.qualityFlags ?? []).map((flag) => (
                              <span key={flag} className="chip warn">
                                {labels[flag] ?? flag}
                              </span>
                            ))}
                          </div>
                          {task.qualityNote}
                          <div className="muted">
                            {task.qualitySource?.includes('lokal') && task.qualitySource !== 'lokal' ? 'Lokale Prüfung und KI-Vorschlag' : task.qualitySource === 'lokal' ? 'Lokale Prüfung' : 'KI-Vorschlag'}
                          </div>
                        </td>
                        {hasScores && <td className="nowrap">{task.qualityScore ?? ''}</td>}
                        <td className="nowrap" style={{ textAlign: 'right' }}>
                          {task.qualityStatus === 'vorschlag' ? (
                            <>
                              <button type="button" className="btn secondary small" onClick={() => review.mutate({ id: task.id, status: 'bestaetigt' })}>
                                Bestätigen
                              </button>{' '}
                              <button type="button" className="btn ghost small" onClick={() => review.mutate({ id: task.id, status: 'verworfen' })}>
                                Verwerfen
                              </button>
                            </>
                          ) : (
                            <span className="badge">{STATUS_LABEL[task.qualityStatus ?? ''] ?? ''}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))' }}>
            <section className="card">
              <div className="card-head">
                <h2>
                  <Icon name="qr" size={16} /> Rückmeldungen vom Aushang
                </h2>
              </div>
              {data.feedback.length === 0 ? (
                <EmptyState title="Noch keine Rückmeldungen" hint="Aushänge mit QR-Code richten Sie bei der Liegenschaft unter Objekte ein." />
              ) : (
                <ul className="feed">
                  {data.feedback.map((f) => (
                    <li key={f.id}>
                      <span className={`badge ${f.rating === 'schlecht' ? 'missed' : f.rating === 'gut' ? 'done' : 'warn'}`}>
                        {f.rating === 'gut' ? 'sauber' : f.rating === 'schlecht' ? 'nicht sauber' : 'Meldung'}
                      </span>
                      <div>
                        <div className="small">
                          <Link to={`/objekte/${f.objectId}`}>{f.siteName ?? 'Objekt'}</Link>
                          {f.topic ? `, ${f.topic}` : ''}
                        </div>
                        {f.text && <div>{f.text}</div>}
                        <div className="muted small">
                          {formatDateTime(f.createdAt)}
                          {f.issueId ? ', als Mangel angelegt' : ''}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="card">
              <div className="card-head">
                <h2>
                  <Icon name="key" size={16} /> Wiederholt kein Zutritt (90 Tage)
                </h2>
              </div>
              {data.access.length === 0 ? (
                <EmptyState title="Keine Zutrittsprobleme" hint="Erkannt aus Textbausteinen wie „kein Zugang“ oder „nicht angetroffen“." />
              ) : (
                <ul className="feed">
                  {data.access.map((a) => (
                    <li key={a.objectId}>
                      <span className="badge warn">{a.count}×</span>
                      <div>
                        <Link to={`/objekte/${a.objectId}`} className="strong">
                          {a.siteName}
                          {a.path?.length ? `, ${a.path.join(' › ')}` : ''}
                        </Link>
                        <div className="muted small">
                          zuletzt {formatDate(a.last)}: {a.lastOption}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={async (message) => {
          if (message) toast(message)
          setOpenTask(null)
          await refresh()
        }}
      />
    </>
  )
}
